"use client";

import {
  Button,
  cn,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Bell, ExternalLinkIcon, XIcon } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  SHOP_NOTIFICATION_TYPE_FILTER_OPTIONS,
  shopNotificationTypeLabel,
} from "@/modules/notifications/domain/notification-type-labels";
import type { ShopNotificationRow } from "@/modules/notifications/domain/repo/list-shop-notifications";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { formatDateTime } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  useMarkAllShopNotificationsReadMutation,
  useMarkShopNotificationReadMutation,
  useShopNotificationsListQuery,
} from "../api/shop-notifications-queries";

const SEVERITY_STYLE: Record<string, { dot: string; label: string }> = {
  normal: { dot: "bg-primary", label: "ປົກກະຕິ" },
  warning: { dot: "bg-amber-500", label: "ເຕືອນ" },
  critical: { dot: "bg-destructive", label: "ວິກິດ" },
};

const UNREAD_OPTIONS = [
  { value: "all", label: "ທັງໝົດ" },
  { value: "unread", label: "ຍັງບໍ່ອ່ານ" },
] as const;

export function ShopNotificationsPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate({ from: "/app/shops/$shopId/notifications" });
  const search = useSearch({
    from: "/app/shops/$shopId/notifications",
  }) as {
    limit?: number;
    offset?: number;
    type?: string;
    unreadOnly?: boolean;
  };

  const limit = search.limit ?? 20;
  const offset = search.offset ?? 0;
  const typeFilter = search.type ?? "all";
  const unreadOnly = search.unreadOnly ?? false;

  const list = useShopNotificationsListQuery(
    shopId,
    {
      limit,
      offset,
      unreadOnly,
      type: typeFilter === "all" ? undefined : typeFilter,
    },
    Boolean(shopId),
  );
  const markRead = useMarkShopNotificationReadMutation(shopId);
  const markAllRead = useMarkAllShopNotificationsReadMutation(shopId);

  const hasFilter = typeFilter !== "all" || unreadOnly;

  const columns: TanstackReactTable.ColumnDef<ShopNotificationRow>[] = [
    createSortableColumn("title", "ຫົວຂໍ້", {
      cell: ({ row }) => (
        <div className="min-w-[180px]">
          <p
            className={cn(
              "font-medium text-sm",
              row.original.isUnread && "font-semibold",
            )}
          >
            {row.original.title}
          </p>
          {row.original.body ? (
            <p className="truncate text-muted-foreground text-xs">
              {row.original.body}
            </p>
          ) : null}
        </div>
      ),
    }),
    {
      id: "type",
      header: "ປະເພດ",
      cell: ({ row }) => (
        <span className="text-sm">
          {shopNotificationTypeLabel(row.original.type)}
        </span>
      ),
    },
    {
      id: "severity",
      header: "ຄວາມຮຸນແຮງ",
      cell: ({ row }) => {
        const style =
          SEVERITY_STYLE[row.original.severity] ?? SEVERITY_STYLE.normal;
        return (
          <span className="inline-flex items-center gap-1.5 text-sm">
            <span className={cn("size-2 rounded-full", style.dot)} />
            {style.label}
          </span>
        );
      },
    },
    createSortableColumn("createdAt", "ເວລາ", {
      cell: ({ row }) => (
        <span className="text-sm">{formatDateTime(row.original.createdAt)}</span>
      ),
    }),
    {
      id: "read",
      header: "ສະຖານະ",
      cell: ({ row }) => (
        <span
          className={cn(
            "text-sm",
            row.original.isUnread ? "text-[#F26522]" : "text-muted-foreground",
          )}
        >
          {row.original.isUnread ? "ຍັງບໍ່ອ່ານ" : "ອ່ານແລ້ວ"}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1"
          onClick={() => {
            if (row.original.isUnread) {
              markRead.mutate(row.original.id);
            }
            void nav({ to: row.original.href });
          }}
        >
          <ExternalLinkIcon className="size-3.5" />
          ເປີດ
        </Button>
      ),
    },
  ];

  return (
    <>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.notifications(shopId)} />
      </Header>

      <Main>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Bell className="size-5 text-muted-foreground" />
            <h1 className="font-heading font-semibold text-xl tracking-tight">
              ການແຈ້ງເຕືອນ
            </h1>
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={markAllRead.isPending || !shopId}
            onClick={() => markAllRead.mutate()}
          >
            ອ່ານທັງໝົດ
          </Button>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <SimpleSelect
            value={typeFilter}
            onValueChange={(value) =>
              nav({
                search: {
                  ...search,
                  offset: 0,
                  type: value === "all" ? undefined : value,
                },
              })
            }
            options={[...SHOP_NOTIFICATION_TYPE_FILTER_OPTIONS]}
            placeholder="ປະເພດ"
            className="w-[200px]"
          />
          <SimpleSelect
            value={unreadOnly ? "unread" : "all"}
            onValueChange={(value) =>
              nav({
                search: {
                  ...search,
                  offset: 0,
                  unreadOnly: value === "unread" ? true : undefined,
                },
              })
            }
            options={[...UNREAD_OPTIONS]}
            placeholder="ສະຖານະ"
            className="w-[160px]"
          />
          {hasFilter ? (
            <Button
              variant="ghost"
              size="sm"
              className="gap-1"
              onClick={() =>
                nav({
                  search: { limit, offset: 0 },
                })
              }
            >
              <XIcon className="size-3.5" />
              ລ້າງຕົວກອງ
            </Button>
          ) : null}
        </div>

        <div className="rounded-xl border bg-card">
          <DataTable<ShopNotificationRow, unknown>
            noDataMessage="ບໍ່ມີການແຈ້ງເຕືອນ"
            columns={columns}
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            totalCount={list.data?.meta.total ?? 0}
            offset={offset}
            limit={limit}
            onPaginationChange={(p) =>
              nav({ search: { ...search, offset: p.offset, limit: p.limit } })
            }
          />
        </div>
      </Main>
    </>
  );
}
